import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { TCharacter, TChaser, TPlayer } from "./character";
import { TMapTile } from "../game/map/mapTypes";
import { TTown } from "./town";
import { gameConfig } from "../game/gameConfig";
import generateTowns from "../game/map/townGenerator";
import { TGameOver } from "./gameOver";
import { generateRandomHorseName, THorse } from "./horse";
import { getRandomCharacters } from "../game/characterSelection/characterSelection";

export type TGamePhase = 
  "startGame" // when the game is getting set up
  | "characterSelection" // selecting a character to play with
  | "mapGeneration" // the next stage of game set up (okay, maybe later we could do this as part of start game)
  | "mapView" // at the moment this is the main game view
  | "travelling" // when the player is traveling between towns
  | "paused" // if the player has 'paused' the game, or really, if they have escaped full screen
  | "gameOver" // when the game is over 
  | "townView" // when the player is in a town
  | "Saloon" // when the player is in a town
  | "Jail" // when the player is in a town
  | "General Store" // when the player is in a town
  | "Telegraph Office" // when the player is in a town
  | "Barber" // when the player is in a town
  | "Undertaker" // when the player is in a town
  | "Blacksmith" // when the player is in a town
  | "Bank" // when the player is in a town
  | "encounter" // 

export type TGameState = {
  Phase: TGamePhase;
  PlayerCharacter?: TPlayer;
  Chasers: TChaser[];
  GameMap: TMapTile[][];
  GameTowns: TTown[];
  GameTime: number;
  LastChaser: number;
  StartingTownIndex: number;
  GameOver: TGameOver;
  Horse: THorse;
  PlayerCharacterOptions: TCharacter[]; // we stash the character options here
  MapGenerated: boolean; // flag to see if the map was generated
  LastEncounter: number
}

interface GameContextProps {
  gameState: TGameState;
  setGameState: (state: TGameState) => void;
  updateGameState: (update: Partial<TGameState>) => void;
  getGameTime: () => string;
}


const GameContext = createContext<GameContextProps | undefined>(undefined);




export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [gameState, setGameStateInternal] = useState<TGameState>(() => ({ 
    Phase: "startGame", 
    GameTime: 0, 
    GameTowns:  generateTowns(gameConfig.NumberOfTowns, gameConfig.MinDistance, gameConfig.EdgeGap, gameConfig.MapWidth, gameConfig.MapHeight), 
    GameMap: ([]), 
    Chasers: [], 
    LastChaser: 0, 
    StartingTownIndex: 0,
    GameOver: { reason: "Alive", endedBy: "" },
    Horse: { Name: generateRandomHorseName(), Stamina: 100, Water: 50, TiedUp: false, Feeding: false, East: true },
    PlayerCharacterOptions: [],
    MapGenerated: false,
    LastEncounter: 0
  }));

  const setGameState = (state: TGameState) => {
    setGameStateInternal(state);
  };


  const updateGameState = (update: Partial<TGameState>) => {
    setGameStateInternal((prev) => ({ ...prev, ...update }));
  };

  const getGameTime = (): string => {
    const startDate = new Date("1885-09-02T10:00:00"); // September 2, 1885, 10:00 AM (start of Back to the Future III)
    const resultDate = new Date(startDate.getTime() + gameState.GameTime * 3600000);
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    return resultDate.toLocaleString("en-US", options); // using en-US locale, but might switch to users at some point
  };

  useEffect(() => {
    if (gameState.Phase === "characterSelection" && gameState.PlayerCharacterOptions.length === 0) {
      const characters = getRandomCharacters(2, gameState.GameTowns);
      updateGameState({ PlayerCharacterOptions: characters });
    }
  }, [gameState.Phase, gameState.GameTowns]);

  return (
    <GameContext.Provider value={{ gameState, setGameState, updateGameState, getGameTime }}>
      {children}
    </GameContext.Provider>
  );
};


export const useGame = (): GameContextProps => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error("useGame must be used within a GameProvider");
  }
  return context;
};
